import React, { useEffect, useState } from 'react';
import { Users, Plus, Search, Calendar, Activity, BedDouble, BarChart3, Building2 } from 'lucide-react';
import DashboardLayout from '../../components/common/DashboardLayout';
import { Card, LoadingState, Badge, Button, Modal, Input, Select, Avatar, DoshaTag, EmptyState, LicensedDoctorBadge, DoctorLicenseModal } from '../../components/common/UI';
import { usersAPI } from '../../services/api';
import toast from 'react-hot-toast';

const NAV_ITEMS = [
  { path: '/admin/dashboard', label: 'Dashboard', icon: Activity },
  { path: '/admin/users', label: 'User Management', icon: Users },
  { path: '/admin/appointments', label: 'Appointments', icon: Calendar },
  { path: '/admin/beds', label: 'Bed Management', icon: BedDouble },
  { path: '/admin/analytics', label: 'Analytics', icon: BarChart3 },
  { path: '/admin/centers', label: 'Centers', icon: Building2 },
];

const fmt = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [roleFilter, setRoleFilter] = useState('');
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toggling, setToggling] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'patient', phone: '', specialization: '', dosha: '' });
  const [selectedDoctorForLicense, setSelectedDoctorForLicense] = useState(null);
  const [updatingLicense, setUpdatingLicense] = useState(false);

  const handleApproveLicense = async () => {
    if (!selectedDoctorForLicense) return;
    setUpdatingLicense(true);
    try {
      await usersAPI.updateLicenseStatus(selectedDoctorForLicense._id, { status: 'approved' });
      toast.success(`Medical License approved for ${selectedDoctorForLicense.name}`);
      setSelectedDoctorForLicense(null);
      load();
    } catch {
      toast.error('Failed to approve license');
    } finally {
      setUpdatingLicense(false);
    }
  };

  const handleRejectLicense = async () => {
    if (!selectedDoctorForLicense) return;
    const reason = prompt('Enter rejection reason for doctor license application:');
    if (reason === null) return;
    setUpdatingLicense(true);
    try {
      await usersAPI.updateLicenseStatus(selectedDoctorForLicense._id, { status: 'rejected', rejectionReason: reason });
      toast.success(`License rejected for ${selectedDoctorForLicense.name}`);
      setSelectedDoctorForLicense(null);
      load();
    } catch {
      toast.error('Failed to reject license');
    } finally {
      setUpdatingLicense(false);
    }
  };

  const load = (role = roleFilter, q = search) => {
    setLoading(true);
    usersAPI.getAll({ role, search: q, limit: 50 })
      .then(r => setUsers(r.data.users || []))
      .catch(() => toast.error('Failed to load users'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleSearch = (e) => {
    setSearch(e.target.value);
    load(roleFilter, e.target.value);
  };

  const handleRoleFilter = (role) => {
    setRoleFilter(role);
    load(role, search);
  };

  const handleCreate = async () => {
    if (!form.name || !form.email || !form.password) return toast.error('Fill required fields');
    setSaving(true);
    try {
      const payload = {
        ...form,
        patientProfile: form.role === 'patient' ? { dosha: form.dosha || 'Vata' } : undefined,
        doctorProfile: form.role === 'doctor' ? { specialization: form.specialization } : undefined,
      };
      await fetch('/api/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('avartana_token')}` },
        body: JSON.stringify(payload)
      });
      toast.success('User created!');
      setShowCreate(false);
      setForm({ name: '', email: '', password: '', role: 'patient', phone: '', specialization: '', dosha: '' });
      load();
    } catch { toast.error('Failed to create user'); }
    finally { setSaving(false); }
  };

  const toggleStatus = async (id) => {
    setToggling(id);
    try {
      await usersAPI.toggleStatus(id);
      setUsers(prev => prev.map(u => u._id === id ? { ...u, isActive: !u.isActive } : u));
      toast.success('Status updated');
    } catch { toast.error('Failed to update'); }
    finally { setToggling(null); }
  };

  const roleColor = { patient: 'emerald', doctor: 'blue', admin: 'violet' };

  return (
    <DashboardLayout navItems={NAV_ITEMS} role="admin">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h1 className="text-2xl font-bold text-gray-800">User Management</h1>
        <Button onClick={() => setShowCreate(true)}><Plus className="w-4 h-4" /> Add User</Button>
      </div>

      {/* Filters */}
      <div className="flex gap-3 mb-5 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input value={search} onChange={handleSearch} placeholder="Search by name or email..." className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-violet-500 bg-white" />
        </div>
        <div className="flex gap-2">
          {['', 'patient', 'doctor', 'admin'].map(r => (
            <button key={r} onClick={() => handleRoleFilter(r)}
              className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${roleFilter === r ? 'bg-violet-600 text-white shadow-sm' : 'bg-white border border-gray-200 text-gray-600 hover:border-gray-300'}`}>
              {r === '' ? 'All' : r.charAt(0).toUpperCase() + r.slice(1) + 's'}
            </button>
          ))}
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-3 gap-4 mb-5">
        {['patient', 'doctor', 'admin'].map(r => {
          const count = users.filter(u => u.role === r).length;
          const colors = { patient: 'emerald', doctor: 'blue', admin: 'violet' };
          return (
            <div key={r} className={`bg-${colors[r]}-50 border border-${colors[r]}-200 rounded-2xl p-4 text-center`}>
              <p className={`text-2xl font-bold text-${colors[r]}-600`}>{count}</p>
              <p className={`text-sm text-${colors[r]}-700 capitalize`}>{r}s</p>
            </div>
          );
        })}
      </div>

      {loading ? <LoadingState /> : (
        <Card>
          {users.length === 0 ? <EmptyState icon={Users} title="No users found" description="Try adjusting your filters" /> : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-100">
                    <th className="text-left text-xs font-bold text-gray-400 uppercase tracking-wider pb-3">User</th>
                    <th className="text-left text-xs font-bold text-gray-400 uppercase tracking-wider pb-3">Role</th>
                    <th className="text-left text-xs font-bold text-gray-400 uppercase tracking-wider pb-3 hidden md:table-cell">Details</th>
                    <th className="text-left text-xs font-bold text-gray-400 uppercase tracking-wider pb-3 hidden lg:table-cell">Joined</th>
                    <th className="text-left text-xs font-bold text-gray-400 uppercase tracking-wider pb-3">Status</th>
                    <th className="text-left text-xs font-bold text-gray-400 uppercase tracking-wider pb-3">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {users.map(u => (
                    <tr key={u._id} className="hover:bg-gray-50 transition-colors">
                      <td className="py-3 pr-4">
                        <div className="flex items-center gap-3">
                          <Avatar name={u.name} color={roleColor[u.role] || 'emerald'} size="sm" />
                          <div>
                            <p className="text-sm font-semibold text-gray-800">{u.name}</p>
                            <p className="text-xs text-gray-400">{u.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 pr-4">
                        <Badge color={roleColor[u.role] || 'gray'}>{u.role}</Badge>
                      </td>
                      <td className="py-3 pr-4 hidden md:table-cell">
                        {u.role === 'patient' && u.patientProfile?.dosha && <DoshaTag dosha={u.patientProfile.dosha} />}
                        {u.role === 'doctor' && (
                          <div className="space-y-1">
                            <p className="text-xs text-gray-500">{u.doctorProfile?.specialization || '—'}</p>
                            <LicensedDoctorBadge doctor={u} onClick={() => setSelectedDoctorForLicense(u)} />
                          </div>
                        )}
                        <p className="text-xs text-gray-400 mt-0.5">{u.phone || '—'}</p>
                      </td>
                      <td className="py-3 pr-4 hidden lg:table-cell">
                        <p className="text-xs text-gray-500">{fmt(u.createdAt)}</p>
                        {u.lastLogin && <p className="text-xs text-gray-400">Last: {fmt(u.lastLogin)}</p>}
                      </td>
                      <td className="py-3 pr-4">
                        <Badge color={u.isActive ? 'emerald' : 'red'}>{u.isActive ? 'Active' : 'Inactive'}</Badge>
                      </td>
                      <td className="py-3 flex items-center gap-2">
                        <Button size="sm" variant={u.isActive ? 'danger' : 'primary'}
                          loading={toggling === u._id} onClick={() => toggleStatus(u._id)}>
                          {u.isActive ? 'Deactivate' : 'Activate'}
                        </Button>
                        {u.role === 'doctor' && (
                          <Button size="sm" variant="outline" onClick={() => setSelectedDoctorForLicense(u)}>
                            Review License
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {/* Create User Modal */}
      <Modal isOpen={showCreate} onClose={() => setShowCreate(false)} title="Add New User" size="md">
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Input label="Full Name *" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Full name" />
            <Input label="Phone" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="+91-XXXXXXXXXX" />
            <div className="col-span-2">
              <Input label="Email *" type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="email@example.com" />
            </div>
            <Input label="Password *" type="password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} placeholder="Min 6 chars" />
            <Select label="Role *" value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))}>
              <option value="patient">Patient</option>
              <option value="doctor">Doctor</option>
              <option value="admin">Admin</option>
            </Select>
          </div>
          {form.role === 'doctor' && (
            <Input label="Specialization" value={form.specialization} onChange={e => setForm(f => ({ ...f, specialization: e.target.value }))} placeholder="e.g., Panchakarma Specialist" />
          )}
          {form.role === 'patient' && (
            <Select label="Dosha" value={form.dosha} onChange={e => setForm(f => ({ ...f, dosha: e.target.value }))}>
              <option value="">Select dosha</option>
              <option value="Vata">Vata</option>
              <option value="Pitta">Pitta</option>
              <option value="Kapha">Kapha</option>
              <option value="Tridosha">Tridosha</option>
            </Select>
          )}
          <div className="flex gap-3 justify-end pt-2 border-t">
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={handleCreate} loading={saving} className="bg-violet-600 hover:bg-violet-700">Create User</Button>
          </div>
        </div>
      </Modal>

      {/* Admin Doctor License Inspection & Approval Modal */}
      <DoctorLicenseModal
        isOpen={!!selectedDoctorForLicense}
        onClose={() => setSelectedDoctorForLicense(null)}
        doctor={selectedDoctorForLicense}
        onAdminApprove={handleApproveLicense}
        onAdminReject={handleRejectLicense}
        actionLoading={updatingLicense}
      />
    </DashboardLayout>
  );
}
